if (!customElements.get("eck-ymm-filter")) {
  customElements.define(
    "eck-ymm-filter",
    class EckYmmFilter extends HTMLElement {
      // Safety ceiling on data pages fetched (250 products/page). The source
      // collection is ~1.9k products (8 pages) and growing; 20 leaves headroom
      // so no makes/models silently drop out of the dropdowns.
      static MAX_PAGES = 20;

      // Bump to invalidate cached fitment data in returning visitors' sessions
      // (e.g. when the page-fetch coverage changes). v1 cached only 5 pages.
      // ECKv3: v3 — completeness now judged against the TRUE page count, so an
      // incomplete (capped/partial) dataset is never cached as complete.
      // ECKv3.42: display names for model codes and multi-word models that don't title-case cleanly (were "Gv70", "Es350", "Cr-V", "Santafe").
      static MODEL_LABELS = {"ram-1500":"Ram 1500","ram-2500":"Ram 2500","ram-3500":"Ram 3500","ram-4500":"Ram 4500","ram-5500":"Ram 5500","promaster":"ProMaster (Full-Size)","promaster-city":"ProMaster City","gv70":"GV70","gv80":"GV80","electrified-g80":"Electrified G80","electrified-gv70":"Electrified GV70","ct200h":"CT200h","es300":"ES300","es300h":"ES300h","es330":"ES330","es350":"ES350","gs-f":"GS-F","gs200t":"GS200t","gs300":"GS300","gs350":"GS350","gs400":"GS400","gs430":"GS430","gs450h":"GS450h","gs460":"GS460","gx460":"GX460","gx470":"GX470","hs250":"HS250","is-c":"IS-C","is-f":"IS-F","is200t":"IS200t","is250":"IS250","is300":"IS300","is350":"IS350","ls400":"LS400","ls430":"LS430","ls460":"LS460","ls500":"LS500","ls600h":"LS600h","lx470":"LX470","lx570":"LX570","nx200t":"NX200t","nx300":"NX300","nx300h":"NX300h","rc200t":"RC200t","rc300":"RC300","rc350":"RC350","rx300":"RX300","rx330":"RX330","rx350":"RX350","rx400h":"RX400h","rx450h":"RX450h","rx450hl":"RX450hL","sc300":"SC300","sc400":"SC400","sc430":"SC430","ux200":"UX200","ux250h":"UX250h","cl":"CL","el":"EL","rl":"RL","tl":"TL","tt":"TT","i3":"i3","i8":"i8","lacrosse":"LaCrosse","lesabre":"LeSabre","parkavenue":"Park Avenue","cts-v":"CTS-V","deville":"DeVille","cityexpress":"City Express","montecarlo":"Monte Carlo","ss":"SS","grand-voyager":"Grand Voyager","newyorker":"New Yorker","ptcruiser":"PT Cruiser","townandcountry":"Town & Country","500l":"500L","500x":"500X","crown-victoria":"Crown Victoria","ecosport":"EcoSport","fivehundred":"Five Hundred","gt":"GT","transit-connect":"Transit Connect","cr-v":"CR-V","cr-z":"CR-Z","hr-v":"HR-V","fit":"Fit","elantra-gt":"Elantra GT","kona-electric":"Kona Electric","santa-cruz":"Santa Cruz","santafe":"Santa Fe","ex35":"EX35","ex37":"EX37","fx35":"FX35","fx37":"FX37","fx45":"FX45","fx50":"FX50","jx35":"JX35","qx56":"QX56","qx60":"QX60","qx70":"QX70","qx80":"QX80","xe":"XE","xf":"XF","xj":"XJ","xk":"XK","grand-cherokee":"Grand Cherokee","grand-wagoneer":"Grand Wagoneer","rio":"Rio","discovery-sport":"Discovery Sport","rangerover":"Range Rover","rangerover-evoque":"Range Rover Evoque","rangerover-sport":"Range Rover Sport","rangerover-velar":"Range Rover Velar","ls":"LS","mark-lt":"Mark LT","town-car":"Town Car","cx-3":"CX-3","cx-30":"CX-30","cx-5":"CX-5","cx-50":"CX-50","cx-7":"CX-7","cx-70":"CX-70","cx-9":"CX-9","cx-90":"CX-90","mx-5":"MX-5","mx6":"MX-6","rx-8":"RX-8","gl":"GL","sl":"SL","grand-marquis":"Grand Marquis","eclipse-cross":"Eclipse Cross","i-miev":"i-MiEV","240sx":"240SX","350z":"350Z","370z":"370Z","nv-van":"NV Van","versa-note":"Versa Note","grand-am":"Grand Am","trans-sport":"Trans Sport","sky":"Sky","vue":"Vue","fr-s":"FR-S","im":"iM","iq":"iQ","tc":"tC","xb":"xB","xd":"xD","4runner":"4Runner","c-hr":"C-HR","fj-cruiser":"FJ Cruiser","landcruiser":"Land Cruiser","rav4":"RAV4","cc":"CC","eos":"Eos","xc40":"XC40","xc60":"XC60","xc70":"XC70","xc90":"XC90"};
      static MAKE_LABELS = {"bmw":"BMW","gmc":"GMC","ram":"RAM","landrover":"Land Rover","land-rover":"Land Rover","mini-cooper":"Mini Cooper","kia":"Kia"};

      static CACHE_VERSION = 4; // ECKv3.30: v4 — Ram trucks retagged (make:RAM + make:Dodge)

      constructor() {
        super();
        this.products = [];
        this.handleYearChange = this.handleYearChange.bind(this);
        this.handleMakeChange = this.handleMakeChange.bind(this);
        this.handleModelChange = this.handleModelChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
      }

      connectedCallback() {
        this.yearSelect = this.querySelector("[data-ymm-year]");
        this.makeSelect = this.querySelector("[data-ymm-make]");
        this.modelSelect = this.querySelector("[data-ymm-model]");
        this.submitButton = this.querySelector("[data-ymm-submit]");
        this.statusEl = this.querySelector("[data-ymm-status]");

        try {
          this.strings = JSON.parse(this.dataset.strings || "{}");
        } catch (e) {
          this.strings = {};
        }

        this.store = window.EckUtils.safeStorage("session");
        this.sourceHandle = this.dataset.sourceHandle || "all";
        this.dataSection = this.dataset.dataSection || "eck-ymm-data";
        this.redirectBase = (this.dataset.redirectBase || "/collections/all/").replace(/\/$/, "");

        this.resetSelect(this.yearSelect);
        this.resetSelect(this.makeSelect);
        this.resetSelect(this.modelSelect);
        this.setControlsDisabled(true);
        this.setStatus(this.strings.loading, false);

        this.yearSelect.addEventListener("change", this.handleYearChange);
        this.makeSelect.addEventListener("change", this.handleMakeChange);
        this.modelSelect.addEventListener("change", this.handleModelChange);
        if (this.submitButton) this.submitButton.addEventListener("click", this.handleSubmit);

        this.load();
      }

      disconnectedCallback() {
        if (this.yearSelect) this.yearSelect.removeEventListener("change", this.handleYearChange);
        if (this.makeSelect) this.makeSelect.removeEventListener("change", this.handleMakeChange);
        if (this.modelSelect) this.modelSelect.removeEventListener("change", this.handleModelChange);
        if (this.submitButton) this.submitButton.removeEventListener("click", this.handleSubmit);
      }

      setControlsDisabled(disabled) {
        [this.yearSelect, this.makeSelect, this.modelSelect].forEach((el) => {
          if (el) el.disabled = disabled;
        });
      }

      setSubmitEnabled(enabled) {
        if (!this.submitButton) return;
        this.submitButton.disabled = !enabled;
        this.submitButton.setAttribute("aria-disabled", enabled ? "false" : "true");
      }

      resetSelect(select) {
        if (!select) return;
        const placeholder = select.options.length ? select.options[0] : null;
        select.innerHTML = "";
        if (placeholder) select.appendChild(placeholder);
        select.value = "";
        select.disabled = true;
      }

      setStatus(message, isError) {
        if (!this.statusEl) return;
        if (message) {
          this.statusEl.textContent = message;
          this.statusEl.hidden = false;
          this.statusEl.dataset.ymmStatus = isError ? "error" : "ready";
        } else {
          this.statusEl.textContent = "";
          this.statusEl.hidden = true;
          delete this.statusEl.dataset.ymmStatus;
        }
      }

      async load() {
        const key = `eck-ymm:${this.sourceHandle}:v${EckYmmFilter.CACHE_VERSION}`;
        const cached = this.store.read(key, null);
        if (cached) {
          this.products = cached;
          this.onProductsReady();
          return;
        }

        try {
          const first = await this.fetchPage(this.sourceHandle, 1);
          // ECKv3: cap pages we fetch, but remember the TRUE total separately so
          // completeness is judged against the real catalog, not the capped value.
          const truePages = first.totalPages;
          const fetchPages = Math.min(truePages, EckYmmFilter.MAX_PAGES);
          // ECKv3 (Task A): if the catalog has more pages than the cap, the data
          // we load is incomplete by definition — warn so the cap can be raised
          // before dropdowns silently under-represent the collection.
          if (truePages > EckYmmFilter.MAX_PAGES) {
            console.warn(
              `eck-ymm-filter: source collection has ${truePages} pages but MAX_PAGES=${EckYmmFilter.MAX_PAGES}; ` +
                `dropdowns will be incomplete and will NOT be cached. Raise MAX_PAGES.`,
            );
          }
          // Tolerate a single flaky page rather than failing the whole picker:
          // keep every page that resolved. Only cache when the set is complete,
          // so a transient failure retries on the next navigation.
          const settled = await Promise.allSettled(
            Array.from({ length: Math.max(0, fetchPages - 1) }, (_, i) => this.fetchPage(this.sourceHandle, i + 2)),
          );
          const rest = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);
          this.products = first.rows.concat(...rest.map((r) => r.rows));
          // ECKv3 (Task B): warn when a page failed this session so silent
          // under-coverage from a transient fetch error is at least diagnosable.
          if (rest.length !== fetchPages - 1) {
            console.warn(
              `eck-ymm-filter: only ${rest.length + 1}/${fetchPages} pages resolved this session; ` +
                `dropdowns may be under-covered and will NOT be cached (will retry next navigation).`,
            );
          }
          // ECKv3 (Task A+B): cache only when EVERY real page was fetched — both
          // the cap (truePages <= MAX_PAGES) and the partial-fetch checks must
          // hold, so an incomplete set is never served as complete.
          if (truePages <= EckYmmFilter.MAX_PAGES && rest.length === fetchPages - 1) this.cache(key);
          this.onProductsReady();
        } catch (e) {
          try {
            this.products = await this.loadFallback(this.sourceHandle);
            if (this.products.length) {
              this.cache(key);
              this.onProductsReady();
              return;
            }
          } catch (e2) {
            /* fall through to error */
          }
          this.setControlsDisabled(true);
          this.setStatus(this.strings.unavailable, true);
        }
      }

      cache(key) {
        this.store.write(key, this.products);
      }

      async fetchPage(handle, page) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        const url = `${root}collections/${handle}?section_id=${this.dataSection}&page=${page}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("eck-ymm-data fetch failed");
        const doc = new DOMParser().parseFromString(await res.text(), "text/html");
        const node = doc.querySelector("[data-eck-ymm-data]");
        if (!node) throw new Error("eck-ymm-data missing");
        return {
          rows: JSON.parse(node.textContent),
          totalPages: parseInt(node.dataset.totalPages, 10) || 1,
        };
      }

      async loadFallback(handle) {
        const rows = [];
        // ECKv3 (Task C): use the storefront root prefix like fetchPage() so this
        // works on shops served under a sub-path. Caveat: /products.json returns
        // ONLY published products, so this last-resort universe can legitimately
        // differ from the SRA path's — it is a fallback, not an equivalent source.
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        for (let page = 1; page <= EckYmmFilter.MAX_PAGES; page += 1) {
          const res = await fetch(`${root}collections/${handle}/products.json?limit=250&page=${page}`);
          if (!res.ok) break;
          const data = await res.json();
          const products = data.products || [];
          products.forEach((p) => {
            rows.push(
              (p.tags || [])
                .map((t) => String(t).trim().toLowerCase())
                .filter((t) => t.startsWith("year:") || t.startsWith("make:") || t.startsWith("model:")),
            );
          });
          if (products.length < 250) break;
        }
        return rows;
      }

      onProductsReady() {
        if (!this.products.length) {
          this.setControlsDisabled(true);
          this.setStatus(this.strings.unavailable, true);
          return;
        }
        this.setStatus("", false);
        this.populateYears();
      }

      getValues(prefix, filterTags) {
        const set = new Set();
        this.products.forEach((tags) => {
          if (filterTags && !filterTags.every((needed) => tags.indexOf(needed) !== -1)) return;
          tags.forEach((tag) => {
            if (tag.indexOf(prefix) === 0) set.add(tag.slice(prefix.length));
          });
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
      }

      formatLabel(prefix, value) {
        if (prefix === "year:") return value;
        // ECKv3.30: display names for tag values that don't title-case cleanly.
        const labels = prefix === "model:" ? EckYmmFilter.MODEL_LABELS : prefix === "make:" ? EckYmmFilter.MAKE_LABELS : null;
        if (labels && labels[value]) return labels[value];
        if (value.length === 3) return value.toUpperCase();
        return value.replace(/\b\w/g, (c) => c.toUpperCase());
      }

      fillSelect(select, prefix, values) {
        const placeholder = select.options[0];
        select.innerHTML = "";
        select.appendChild(placeholder);
        values.forEach((value) => {
          const option = document.createElement("option");
          option.value = value;
          option.textContent = this.formatLabel(prefix, value);
          select.appendChild(option);
        });
        select.value = "";
      }

      populateYears() {
        const years = this.getValues("year:").reverse();
        this.fillSelect(this.yearSelect, "year:", years);
        this.yearSelect.disabled = false;
        this.resetSelect(this.makeSelect);
        this.resetSelect(this.modelSelect);
        this.setSubmitEnabled(false);
      }

      handleYearChange() {
        this.resetSelect(this.makeSelect);
        this.resetSelect(this.modelSelect);
        this.setSubmitEnabled(false);
        const year = this.yearSelect.value;
        if (!year) return;
        const makes = this.getValues("make:", [`year:${year}`]);
        if (!makes.length) {
          this.setStatus(this.strings.noMakes, true);
          return;
        }
        this.setStatus("", false);
        this.fillSelect(this.makeSelect, "make:", makes);
        this.makeSelect.disabled = false;
      }

      handleMakeChange() {
        this.resetSelect(this.modelSelect);
        this.setSubmitEnabled(false);
        const year = this.yearSelect.value;
        const make = this.makeSelect.value;
        if (!year || !make) return;
        const models = this.getValues("model:", [`year:${year}`, `make:${make}`]);
        if (!models.length) {
          this.setStatus(this.strings.noModels, true);
          return;
        }
        this.setStatus("", false);
        this.fillSelect(this.modelSelect, "model:", models);
        this.modelSelect.disabled = false;
      }

      handleModelChange() {
        if (!this.modelSelect.value) {
          this.setSubmitEnabled(false);
          return;
        }
        this.setSubmitEnabled(true);
        this.redirect();
      }

      handleSubmit() {
        if (this.yearSelect.value && this.makeSelect.value && this.modelSelect.value) {
          this.redirect();
        } else {
          this.setStatus(this.strings.chooseFirst, true);
        }
      }

      redirect() {
        // ECKv3 (Task D): select.value carries the RAW (lowercased) tag body, not
        // formatLabel() display output, so the URL is built from the true tag.
        const year = this.yearSelect.value;
        const make = this.makeSelect.value;
        const model = this.modelSelect.value;
        if (!year || !make || !model) return;
        // ECKv3 (Task D): encode each tag body so a space, '/', '&', etc. in a
        // value can't break the strict tag-path URL. The 'year:'/'make:'/'model:'
        // prefixes and the '+' AND-joiner stay literal (Shopify's tag-path
        // grammar); only the value after the colon is percent-encoded.
        const token = (prefix, value) => `${prefix}:${encodeURIComponent(value)}`;
        const tags = [token("year", year), token("make", make), token("model", model)].join("+");
        window.location.href = `${this.redirectBase}/${tags}`;
      }
    },
  );
}
